import { useCallback, useEffect, useMemo, useState } from 'react'
import type { MetricDataFilters, MetricDataFilterSet, MetricScenarioType } from '../../../hooks/analytics'
import {
  normalizeCountryCode,
  sanitizeMetricDataFilters,
  sanitizeMetricDataFilterSet,
} from '../export/metricDataFilters'
import type { HazardScenarioPreferencesConfig } from './hazardConfig'

const STORAGE_PREFIX = 'climate-dashboard:hazard-scenario-preferences:v1'
const DEFAULT_PREFERENCE_OPTIONS: HazardScenarioPreferencesConfig = {
  scenarioSelectionMode: 'single',
  showReturnPeriodFilters: false,
}

const memoryPreferences = new Map<string, MetricDataFilters>()

type PreferenceUser =
  | {
      id?: number | string | null
      email?: string | null
    }
  | null
  | undefined

type HazardCode = string | null | undefined

function getStorage(): Storage | null {
  if (typeof window === 'undefined') return null

  try {
    return window.localStorage
  } catch {
    return null
  }
}

function getUserStorageId(user: PreferenceUser): string | null {
  if (user?.id !== undefined && user.id !== null && String(user.id).trim()) {
    return `user:${String(user.id).trim()}`
  }

  const email = user?.email?.trim().toLowerCase()
  return email ? `email:${email}` : null
}

function normalizeScenarioTypes(value: unknown, scenarioSelectionMode: 'single' | 'multiple'): MetricScenarioType[] {
  const values = Array.isArray(value) ? value : value != null ? [value] : []
  const scenarioTypes: MetricScenarioType[] = []

  for (const item of values) {
    const normalized = String(item).trim().toLowerCase()
    if (normalized === 'ssp' || normalized === 'rcp') {
      scenarioTypes.push(normalized)
    }
  }

  const uniqueScenarioTypes = Array.from(new Set(scenarioTypes))
  return scenarioSelectionMode === 'single' ? uniqueScenarioTypes.slice(0, 1) : uniqueScenarioTypes
}

function getAllowedScenarioTypeSet(options: HazardScenarioPreferencesConfig): Set<MetricScenarioType> | null {
  const values = options.singleScenarioOptions
    ?.map(option => option.value)
    .filter((value): value is MetricScenarioType => value === 'ssp' || value === 'rcp')

  return values?.length ? new Set(values) : null
}

function sanitizeFilterSetForOptions(
  value: MetricDataFilterSet | null | undefined,
  options: HazardScenarioPreferencesConfig,
): MetricDataFilterSet | undefined {
  const cleaned = sanitizeMetricDataFilterSet(value)
  if (!cleaned) return undefined

  const allowedScenarioTypes = getAllowedScenarioTypeSet(options)
  const scenarioTypes = normalizeScenarioTypes(cleaned.scenario_type, options.scenarioSelectionMode).filter(
    scenarioType => !allowedScenarioTypes || allowedScenarioTypes.has(scenarioType),
  )
  const out: MetricDataFilterSet = {}
  if (scenarioTypes.length) {
    out.scenario_type = scenarioTypes
  }
  if (options.showReturnPeriodFilters && cleaned.return_period) {
    out.return_period = cleaned.return_period
  }

  return Object.keys(out).length > 0 ? out : undefined
}

function normalizeHazardCode(hazardCode: HazardCode): string | null {
  const normalized = String(hazardCode ?? '').trim()
  return normalized || null
}

function sanitizeHazardScenarioPreferences(
  value: MetricDataFilters | null | undefined,
  options: HazardScenarioPreferencesConfig = DEFAULT_PREFERENCE_OPTIONS,
): MetricDataFilters | null {
  const cleaned = sanitizeMetricDataFilters(value)
  if (!cleaned) return null

  const root = sanitizeFilterSetForOptions(cleaned, options)
  const countryOverrides: Record<string, MetricDataFilterSet> = {}

  Object.entries(cleaned.country_overrides ?? {}).forEach(([rawCountryCode, filterSet]) => {
    const countryCode = normalizeCountryCode(rawCountryCode)
    const cleanedFilterSet = sanitizeFilterSetForOptions(filterSet, options)
    if (countryCode && cleanedFilterSet) {
      countryOverrides[countryCode] = cleanedFilterSet
    }
  })

  const out: MetricDataFilters = {}
  if (root?.scenario_type?.length) {
    out.scenario_type = root.scenario_type
  }
  if (Object.keys(countryOverrides).length > 0) {
    out.country_overrides = countryOverrides
  }

  return Object.keys(out).length > 0 ? out : null
}

export function hasHazardScenarioPreferences(
  value: MetricDataFilters | null | undefined,
  options: HazardScenarioPreferencesConfig = DEFAULT_PREFERENCE_OPTIONS,
): boolean {
  return sanitizeHazardScenarioPreferences(value, options) !== null
}

function sanitizeStoredHazardScenarioPreferences(
  value: unknown,
  options: HazardScenarioPreferencesConfig,
): MetricDataFilters | null {
  if (value && typeof value === 'object' && ('filters' in value || 'explicitDefault' in value)) {
    const stored = value as { filters?: unknown; explicitDefault?: unknown }
    return sanitizeHazardScenarioPreferences(stored.filters as MetricDataFilters | null | undefined, options)
  }

  return sanitizeHazardScenarioPreferences(value as MetricDataFilters | null | undefined, options)
}

function buildHazardScenarioPreferenceStorageKey(user: PreferenceUser, hazardCode: HazardCode): string | null {
  const userId = getUserStorageId(user)
  const normalizedHazardCode = normalizeHazardCode(hazardCode)
  return userId && normalizedHazardCode ? `${STORAGE_PREFIX}:${userId}:${normalizedHazardCode}` : null
}

export function useHazardScenarioPreferences(
  user: PreferenceUser,
  hazardCode: HazardCode,
  options?: HazardScenarioPreferencesConfig,
) {
  const userId = user?.id
  const userEmail = user?.email
  const normalizedHazardCode = normalizeHazardCode(hazardCode)
  const scenarioPreferencesEnabled = options != null
  const scenarioSelectionMode = options?.scenarioSelectionMode ?? DEFAULT_PREFERENCE_OPTIONS.scenarioSelectionMode
  const showReturnPeriodFilters = options?.showReturnPeriodFilters ?? DEFAULT_PREFERENCE_OPTIONS.showReturnPeriodFilters
  const singleScenarioOptions = options?.singleScenarioOptions
  const sanitizePreferences = useCallback(
    (value: MetricDataFilters | null | undefined) =>
      sanitizeHazardScenarioPreferences(value, {
        scenarioSelectionMode,
        showReturnPeriodFilters,
        singleScenarioOptions,
      }),
    [scenarioSelectionMode, showReturnPeriodFilters, singleScenarioOptions],
  )
  const sanitizeStoredPreferences = useCallback(
    (value: unknown) =>
      sanitizeStoredHazardScenarioPreferences(value, {
        scenarioSelectionMode,
        showReturnPeriodFilters,
        singleScenarioOptions,
      }),
    [scenarioSelectionMode, showReturnPeriodFilters, singleScenarioOptions],
  )
  const storageKey = useMemo(
    () =>
      scenarioPreferencesEnabled
        ? buildHazardScenarioPreferenceStorageKey({ id: userId, email: userEmail }, normalizedHazardCode)
        : null,
    [normalizedHazardCode, scenarioPreferencesEnabled, userEmail, userId],
  )
  const [filters, setFilters] = useState<MetricDataFilters | null>(null)

  useEffect(() => {
    if (!storageKey) {
      setFilters(null)
      return
    }

    const storage = getStorage()
    if (!storage) {
      setFilters(memoryPreferences.get(storageKey) ?? null)
      return
    }

    try {
      const raw = storage.getItem(storageKey)
      const storedFilters = raw ? sanitizeStoredPreferences(JSON.parse(raw)) : null
      if (storedFilters) {
        memoryPreferences.set(storageKey, storedFilters)
        storage.setItem(storageKey, JSON.stringify(storedFilters))
      } else {
        if (raw) {
          storage.removeItem(storageKey)
        }
        memoryPreferences.delete(storageKey)
      }
      setFilters(storedFilters)
    } catch {
      memoryPreferences.delete(storageKey)
      setFilters(null)
    }
  }, [sanitizeStoredPreferences, storageKey])

  const saveFilters = useCallback(
    (nextFilters: MetricDataFilters | null) => {
      const cleaned = sanitizePreferences(nextFilters)
      setFilters(cleaned)

      if (!storageKey) return
      if (cleaned) {
        memoryPreferences.set(storageKey, cleaned)
      } else {
        memoryPreferences.delete(storageKey)
      }

      const storage = getStorage()
      if (!storage) return

      try {
        if (cleaned) {
          storage.setItem(storageKey, JSON.stringify(cleaned))
        } else {
          storage.removeItem(storageKey)
        }
      } catch {
        // Keep the in-memory value when persistent browser storage is unavailable.
      }
    },
    [sanitizePreferences, storageKey],
  )

  return {
    filters,
    saveFilters,
  }
}
